import type { VaultMeta } from "../api/vault.js";
import { daysUntilExpiry, isAutofillable } from "../vaultPayload.js";
import type { ItemType, VaultPayload } from "../vaultPayload.js";

export interface Entry {
  readonly meta: VaultMeta;
  readonly data: VaultPayload;
}

export interface EntryFilter {
  readonly query?: string | undefined;
  readonly favoritesOnly?: boolean | undefined;
  readonly folder?: string | undefined;
  readonly ids?: ReadonlySet<string> | undefined;
  readonly type?: ItemType | undefined;
}

/** Items whose expiry date is at most this many days away (or already past) count as expiring. */
export const EXPIRY_WARNING_DAYS = 30;

export type SortOrder = "updated" | "title";

export interface FolderCount {
  readonly name: string;
  readonly count: number;
}

export function matchesQuery(entry: Entry, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === "") {
    return true;
  }
  const haystack = `${entry.data.title} ${entry.data.username} ${entry.data.url} ${entry.data.fields.map((f) => f.label).join(" ")}`.toLowerCase();
  return haystack.includes(needle);
}

export function filterEntries(entries: ReadonlyArray<Entry>, filter: EntryFilter): Entry[] {
  return entries.filter((entry: Entry): boolean => {
    if (filter.favoritesOnly === true && !entry.meta.favorite) {
      return false;
    }
    if (filter.folder !== undefined && entry.meta.folder !== filter.folder) {
      return false;
    }
    if (filter.ids !== undefined && !filter.ids.has(entry.meta.id)) {
      return false;
    }
    if (filter.type !== undefined && entry.data.type !== filter.type) {
      return false;
    }
    return filter.query === undefined || matchesQuery(entry, filter.query);
  });
}

export function sortEntries(entries: ReadonlyArray<Entry>, order: SortOrder): Entry[] {
  const sorted = [...entries];
  if (order === "title") {
    sorted.sort((a: Entry, b: Entry): number =>
      a.data.title.localeCompare(b.data.title, "de", { sensitivity: "base" })
    );
  } else {
    sorted.sort((a: Entry, b: Entry): number => b.meta.updatedAt - a.meta.updatedAt);
  }
  return sorted;
}

export function folderCounts(entries: ReadonlyArray<Entry>): FolderCount[] {
  const counts = new Map<string, number>();
  for (const entry of entries) {
    counts.set(entry.meta.folder, (counts.get(entry.meta.folder) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([name, count]: [string, number]): FolderCount => ({ name, count }))
    .sort((a: FolderCount, b: FolderCount): number => a.name.localeCompare(b.name, "de"));
}

/** Password health only applies to logins; API secrets and license keys are not chosen by people. */
export function healthInputs(entries: ReadonlyArray<Entry>): Array<{ readonly id: string; readonly password: string; readonly updatedAt: number }> {
  return entries
    .filter((e: Entry): boolean => isAutofillable(e.data))
    .map((e: Entry) => ({ id: e.meta.id, password: e.data.password, updatedAt: e.meta.updatedAt }));
}

export function expiringIds(entries: ReadonlyArray<Entry>, nowMs: number): string[] {
  return entries
    .filter((e: Entry): boolean => {
      const days = daysUntilExpiry(e.data, nowMs);
      return days !== undefined && days <= EXPIRY_WARNING_DAYS;
    })
    .map((e: Entry): string => e.meta.id);
}
